// What a failed section shows in place of itself. The recovery action lives
// HERE and persists: the floating prompt can be dismissed so someone can
// reach a save button elsewhere, and dismissing it must not strand them with
// a blank panel and no way back.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { currentHolds, summarise, holdReason } from './dirtyRegistry';
import { planRecovery, readAttempts, noteAttempt } from './recoveryState';

export const SectionFailed: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { t } = useTranslation('common');
  const holds = summarise(currentHolds());
  const plan = planRecovery(
    { trigger: 'chunk-error', online: navigator.onLine, unsaved: holds.unsaved,
      pending: holds.pending, otherTabsBlocking: false, reloadAttempts: readAttempts('chunk-error') },
    holdReason(holds), null,
  );

  return (
    <div className={`rounded-xl border border-warning/40 bg-warning-soft ${compact ? 'p-3' : 'p-5'}`}
         data-testid="section-failed">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{plan.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{plan.body}</p>
          {plan.blockedReason && (
            <p className="mt-2 text-sm text-warning">{plan.blockedReason}</p>
          )}
          <Button
            size="sm"
            className="mt-3 gap-2"
            disabled={!plan.canReload}
            onClick={() => { noteAttempt('chunk-error'); window.location.reload(); }}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            {t('recovery.reload', 'Reload app')}
          </Button>
        </div>
      </div>
    </div>
  );
};
